import { useParams } from 'react-router';

import { NEW_VIEW } from '@/constants/dashboardConstants';
import { ProjectTemplateSummary } from '@experiment-management-shared/types';
import { useGenerateUnsavedViewId } from '@API/project/useGenerateUnsavedViewTemplateId';
import useDeepMemo from '@shared/hooks/useDeepMemo';
import useProject from './useProject';
import useProjectViews from '@API/project/useProjectViews';
import useProjectViewDetails from './useProjectViewDetails';
import { useMemo } from 'react';

export const SELECTED_VIEW_REFETCH_INTERVAL = 30000;

export default function useSelectedProjectView({
  viewId: paramViewId
}: {
  viewId?: string;
} = {}) {
  const { viewId: pathViewId } = useParams<{ viewId?: string }>();
  const { data: project } = useProject();
  const unsavedTemplateId = useGenerateUnsavedViewId();
  const { data: views = [], isSuccess: viewsLoaded } = useProjectViews({
    refetchInterval: SELECTED_VIEW_REFETCH_INTERVAL
  });
  const projectId = project?.projectId;
  const viewId = pathViewId ?? paramViewId;

  const basicSelectedView = useDeepMemo(() => {
    if (!viewId || !views || !projectId) return;

    // 1. selected personal view
    if (viewId === NEW_VIEW) {
      return (
        views.find(
          view =>
            view.templateId === unsavedTemplateId &&
            view.projectId === projectId &&
            view.unsavedView
        ) || null
      );
    }

    const savedViewList = views.filter(view => !view.unsavedView);

    // 2. selected view id
    const selectedView = savedViewList.find(
      (view: ProjectTemplateSummary) => view.templateId === viewId
    );

    if (viewId && selectedView) {
      return selectedView;
    }

    // 3. project view (not auto-generated)
    const projectView = savedViewList.find(
      (view: ProjectTemplateSummary) => view.projectDefault
    );

    if (projectView?.templateId) {
      return projectView;
    }

    return null;
  }, [projectId, viewId, views]);

  const templateIdToFetch = useMemo(() => {
    return basicSelectedView?.templateId || null;
  }, [basicSelectedView]);

  const {
    data: detailedView,
    isSuccess: detailsLoaded
  } = useProjectViewDetails(
    {
      templateId: templateIdToFetch,
      viewProjectId: basicSelectedView?.projectId
    },
    { enabled: !!templateIdToFetch }
  );

  const selectedView = useMemo(() => {
    if (detailedView && templateIdToFetch) {
      return detailedView;
    }

    return null;
  }, [basicSelectedView, detailedView, templateIdToFetch]);

  const isSuccess = viewsLoaded && (!templateIdToFetch || detailsLoaded);

  return { data: selectedView, isSuccess };
}
